import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api.js';
import { chargerQrProblem } from '../../config/index.js';
import QrScanner from '../../components/QrScanner.jsx';
import { usePublicConfig } from '../../config/demo.js';
import RecentSessions, { ReportProblem } from './RecentSessions.jsx';
import './live-charging-telemetry.scss';

const BLUE = { color: '#005293' };
const AMBER = { color: '#d97706' };
const POLL_MS = 5000;
const peso = (v) => `₱${Number(v ?? 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const clock = (s) => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((x) => String(x).padStart(2, '0')).join(':');
const hhmm = (iso) => new Date(iso).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', hour12: false });
const hhmmss = (iso) => new Date(iso).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
const PLUG_LABEL = { CCS2: 'CCS2', Type2: 'Type 2', CHAdeMO: 'CHAdeMO', GBT: 'GB/T' };

/** Client-side first line of defence: authorized host + canonical path shape (the API re-verifies the HMAC). */
const precheckQr = chargerQrProblem;

function PowerChart({ series }) {
  const ref = useRef(null);
  const plotly = useRef(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!plotly.current) plotly.current = (await import('plotly.js-dist-min')).default;
      if (cancelled || !ref.current) return;
      const trace = {
        x: series.map((p) => hhmmss(p.at)),
        y: series.map((p) => p.powerKw),
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Power (kW)',
        line: { color: '#005293', width: 3 },
        marker: { color: '#005293', size: 7 },
      };
      const layout = {
        paper_bgcolor: 'transparent',
        plot_bgcolor: 'transparent',
        margin: { t: 20, r: 20, b: 40, l: 45 },
        xaxis: { color: '#64748b', gridcolor: '#e2e8f0', showgrid: true },
        yaxis: { color: '#64748b', gridcolor: '#e2e8f0', title: 'Power (kW)', rangemode: 'tozero' },
      };
      plotly.current.react(ref.current, [trace], layout, { responsive: true, displayModeBar: false });
    })();
    return () => {
      cancelled = true;
    };
  }, [series]);
  useEffect(() => {
    const node = ref.current;
    return () => {
      if (plotly.current && node) plotly.current.purge(node);
    };
  }, []);
  return <div ref={ref} id="plotly-charging-chart" style={{ height: 320, width: '100%' }} />;
}

/** Pre-session panel: choose a connector or scan/verify a QR code, then start charging. */
function StartPanel({ siteId, onStarted, onPickSite, initialQr = '', autoScan = false }) {
  const { sessionHoldAmount } = usePublicConfig();
  const [station, setStation] = useState(null);
  const [connectorId, setConnectorId] = useState('');
  const [qrText, setQrText] = useState('');
  const [qrResult, setQrResult] = useState(null); // { valid, connectorId, ... } | { valid:false, warning }
  const [qrBusy, setQrBusy] = useState(false);
  const [scanning, setScanning] = useState(autoScan);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    api.getStation(siteId).then(setStation).catch(() => setError('Could not load that station.'));
  }, [siteId]);

  const verifyQr = async (text = qrText) => {
    setError('');
    setQrResult(null);
    const local = precheckQr(text);
    setQrBusy(true);
    try {
      // The server always verifies (and logs a security incident with geolocation when available).
      let geo = {};
      if (local && navigator.geolocation) {
        geo = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition((p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }), () => resolve({}), { timeout: 1500 });
        });
      }
      const r = await api.verifyQr({ payload: text.trim(), ...geo });
      setQrResult(r);
      if (r.valid) setConnectorId(r.connectorId);
    } catch (e) {
      setError(e.message);
    } finally {
      setQrBusy(false);
    }
  };

  // A code read by the camera (here, or handed over from the scan button elsewhere) is verified straight away.
  const onScanned = (text) => {
    setScanning(false);
    setQrText(text);
    verifyQr(text);
  };
  useEffect(() => {
    if (initialQr) onScanned(initialQr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQr]);

  const start = async () => {
    setError('');
    setStarting(true);
    try {
      const body = qrResult?.valid && qrResult.connectorId === connectorId ? { qrPayload: qrText.trim() } : { connectorId };
      onStarted(await api.startSession(body));
    } catch (e) {
      setError(e.message);
      setStarting(false);
    }
  };

  // Nothing chosen yet: offer stations right here, so the page never dead-ends.
  const [nearby, setNearby] = useState([]);
  useEffect(() => {
    if (siteId) return;
    let live = true;
    const load = (pos) => api.request(`/public/stations?pageSize=6&status=Available${pos ? `&lat=${pos.coords.latitude.toFixed(3)}&lng=${pos.coords.longitude.toFixed(3)}` : ''}`)
      .then((list) => live && setNearby(Array.isArray(list) ? list : [])).catch(() => live && setNearby([]));
    // Nearest first when location is already allowed; this page never asks for it itself.
    const allowed = navigator.permissions?.query ? navigator.permissions.query({ name: 'geolocation' }).then((p) => p.state === 'granted').catch(() => false) : Promise.resolve(false);
    allowed.then((ok) => {
      if (ok) navigator.geolocation.getCurrentPosition(load, () => load(null), { timeout: 6000, maximumAge: 120000 });
      else load(null);
    });
    return () => { live = false; };
  }, [siteId]);
  const [manualOpen, setManualOpen] = useState(() => window.innerWidth >= 992); // phones scan; typing the address is the fallback

  const connectors = station?.connectors || [];
  const chosen = connectors.find((c) => c.id === connectorId);
  const viaQr = qrResult?.valid && qrResult.connectorId === connectorId;
  let hint = 'Scan the charger or choose a connector to continue.';
  if (viaQr) hint = `${qrResult.siteName} · ${PLUG_LABEL[qrResult.connectorType] || qrResult.connectorType} ${qrResult.maxPowerKw} kW`;
  else if (chosen) hint = `${station.name} · ${PLUG_LABEL[chosen.type] || chosen.type} ${chosen.maxPowerKw} kW${station.tariff ? ` · ${peso(station.tariff.energyRate)}/kWh` : ''}`;
  else if (station) hint = 'Choose an available connector to continue.';

  return (
    <div className="container-fluid py-4" id="start-charge-page">
      <div className="card p-4 mb-3 shadow-sm border bg-white start-intro" style={{ borderLeft: '6px solid #005293' }}>
        <h2 className="h4 fw-bold mb-1" style={BLUE}>Start a Charging Session</h2>
        <p className="text-muted small mb-0">Scan the QR code on the charger, or pick a station and connector. A {peso(sessionHoldAmount).replace('.00', '')} pre-authorization hold is placed on your wallet before the charger is contacted.</p>
      </div>
      <div className="row">
        <div className="col-12 col-lg-6 mb-3 mb-lg-4">
          <div className="card p-3 h-100 shadow-sm border bg-white">
            <h5 className="fw-bold mb-1" style={BLUE}><span className="start-step">1</span>At the charger? Scan it</h5>
            <p className="text-muted small mb-3">The QR sticker is next to the connector. Scanning picks the exact connector for you.</p>
            <button type="button" id="btn-scan-qr" className="btn btn-primary btn-lg w-100 fw-bold" onClick={() => setScanning(true)}>
              <i className="fa fa-qrcode me-2" aria-hidden="true" />Scan charger QR code
            </button>
            {qrBusy && <div className="small text-muted mt-2"><span className="spinner-border spinner-border-sm me-2" />Checking the code…</div>}
            {qrResult && !qrResult.valid && (
              <div className="alert alert-danger mt-3 mb-0 fw-bold" role="alert" id="qr-warning">
                <i className="fa fa-triangle-exclamation me-2" />{qrResult.warning}
                <div className="small fw-normal mt-1">{qrResult.reason}. This attempt has been logged.</div>
              </div>
            )}
            {qrResult?.valid && (
              <div className="alert alert-success mt-3 mb-0" id="qr-verified">
                <i className="fa fa-circle-check me-2" />Genuine ChargeOS QR: {qrResult.siteName} &bull; {qrResult.chargerId} &bull; {PLUG_LABEL[qrResult.connectorType] || qrResult.connectorType} ({qrResult.maxPowerKw} kW) &mdash; {qrResult.status}
              </div>
            )}
            <button type="button" className="btn btn-link btn-sm px-0 mt-3 text-start start-manual-toggle" id="btn-manual-qr" aria-expanded={manualOpen} aria-controls="qr-manual" onClick={() => setManualOpen((o) => !o)}>
              <i className={`fa ${manualOpen ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} aria-hidden="true" />Camera not working? Enter the code address
            </button>
            <div id="qr-manual" className={manualOpen ? '' : 'd-none'}>
              <label htmlFor="qr-payload" className="form-label small fw-semibold mt-2">QR code address</label>
              <input id="qr-payload" className="form-control mb-2" placeholder="https://…/c/{chargerId}/{connector}?s=…&n=…" value={qrText} onChange={(e) => { setQrText(e.target.value); setQrResult(null); }} autoComplete="off" />
              <button type="button" id="btn-verify-qr" className="btn btn-outline-primary" disabled={!qrText.trim() || qrBusy} onClick={() => verifyQr()}>
                <i className="fa fa-shield-halved me-2" aria-hidden="true" />Verify QR
              </button>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6 mb-3 mb-lg-4">
          <div className="card p-3 h-100 shadow-sm border bg-white">
            <h5 className="fw-bold mb-1" style={BLUE}><span className="start-step">2</span>{station ? station.name : 'Or choose a station'}</h5>
            {station && <p className="text-muted small mb-3">{station.address}, {station.city} &middot; <Link to="/station-locator-availability">change station</Link></p>}
            {!siteId && (
              <>
                <p className="text-muted small mb-3">Not at the charger yet? Pick where you are going.</p>
                <div id="start-station-list">
                  {nearby.map((st) => {
                    const free = st.connectors.filter((c) => c.status === 'Available').length;
                    return (
                      <button type="button" key={st.id} className="start-station" data-site-id={st.id} onClick={() => onPickSite(st.id)}>
                        <span className="start-station-main">
                          <strong>{st.name}</strong>
                          <span className="text-muted small">{st.city}{st.distanceKm != null ? ` · ${st.distanceKm < 10 ? st.distanceKm.toFixed(1) : Math.round(st.distanceKm)} km` : ''}{st.tariff ? ` · ${peso(st.tariff.energyRate)}/kWh` : ''}</span>
                        </span>
                        <span className={`badge ${free ? 'badge-available' : 'badge-faulted'}`}>{free} free</span>
                        <i className="fa fa-chevron-right text-muted ms-2" aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
                <Link to="/station-locator-availability" className="btn btn-outline-primary w-100 mt-2" id="btn-open-map"><i className="fa fa-map-location-dot me-2" aria-hidden="true" />See all stations on the map</Link>
              </>
            )}
            {connectors.map((c) => {
              const ok = c.status === 'Available';
              return (
                <div key={c.id} data-testid="connector-option" role="presentation" className={`connector-option p-2 mb-2 ${connectorId === c.id ? 'selected' : ''} ${ok ? '' : 'disabled'}`} onClick={() => ok && setConnectorId(c.id)} onKeyDown={(e) => { if (ok && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setConnectorId(c.id); } }}>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" name="connector" id={`connector-${c.id}`} disabled={!ok} checked={connectorId === c.id} onChange={() => setConnectorId(c.id)} />
                    <label className="form-check-label fw-semibold" htmlFor={`connector-${c.id}`}>{c.chargePoint} &bull; #{c.index} &bull; {PLUG_LABEL[c.type] || c.type} ({c.maxPowerKw} kW)</label>
                    <span className={`badge ms-2 ${ok ? 'badge-available' : 'badge-faulted'}`}>{ok ? 'Available' : c.status}</span>
                  </div>
                </div>
              );
            })}
            {station?.tariff && <p className="text-muted small mt-2 mb-0">Energy rate: <strong style={BLUE}>{peso(station.tariff.energyRate)} / kWh</strong></p>}
          </div>
        </div>
      </div>
      <div className="start-action-bar">
        {error && <div className="alert alert-danger mb-2" role="alert" id="start-error"><i className="fa fa-circle-exclamation me-2" />{error}</div>}
        <div className="start-action-row">
          <div className={`start-action-hint${connectorId ? ' ready' : ''}`} id="start-hint">{hint}</div>
          <button type="button" id="btn-start-session" className="btn btn-primary btn-lg fw-bold px-4" disabled={!connectorId || starting} onClick={start}>
            {starting ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="fa fa-bolt me-2" aria-hidden="true" />}Start Charging
          </button>
        </div>
      </div>
      {scanning && <QrScanner onResult={onScanned} onClose={() => setScanning(false)} />}
    </div>
  );
}

function SessionSummary({ s }) {
  const audit = s.status === 'audit_hold';
  const c = s.cdr;
  const [reporting, setReporting] = useState(false);
  const [filed, setFiled] = useState(false);
  return (
    <div className="container-fluid py-4">
      <div className="card p-4 shadow-sm border bg-white" id="session-summary" style={{ borderLeft: `6px solid ${audit ? '#d97706' : '#10b981'}` }}>
        <h2 className="h4 fw-bold mb-1" style={BLUE}>{audit ? 'Session Under Audit' : 'Charging Complete'}</h2>
        <p className="text-muted small">{s.sessionCode} &bull; {s.station.siteName} &bull; {s.station.chargePointCode} #{s.station.connectorIndex}</p>
        {audit ? (
          <div className="alert alert-warning" role="alert">
            Billing is suspended and your ₱{s.holdAmount.toFixed(2)} hold is retained while Customer Care audits unusual meter readings{s.anomalies[0] ? ` (ticket ${s.anomalies[0].ticketReference})` : ''}.
          </div>
        ) : (
          <div className="alert alert-success" role="alert">Your Charge Detail Record ({c?.cdrNumber}) has been generated and {peso(c?.settled)} was debited from your prepaid wallet.</div>
        )}
        <div className="row text-center mb-3">
          <div className="col-6 col-md-3"><div className="text-muted small">Energy</div><div className="h4 fw-bold" id="summary-energy">{s.telemetry.energyKwh.toFixed(2)} kWh</div></div>
          <div className="col-6 col-md-3"><div className="text-muted small">Duration</div><div className="h4 fw-bold">{clock(s.elapsedSeconds)}</div></div>
          <div className="col-6 col-md-3"><div className="text-muted small">Final SoC</div><div className="h4 fw-bold">{s.telemetry.socPercent == null ? '—' : `${Math.round(s.telemetry.socPercent)}%`}</div></div>
          <div className="col-6 col-md-3"><div className="text-muted small">Total</div><div className="h4 fw-bold" style={AMBER} id="summary-total">{audit ? 'Pending audit' : peso(c?.total)}</div></div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Link to="/wallet-top-up-payment" className="btn btn-outline-primary">View wallet</Link>
          <Link to="/station-locator-availability" className="btn btn-primary">Find stations</Link>
          <Link to="/live-charging-telemetry" className="btn btn-outline-secondary" id="btn-all-sessions">All sessions</Link>
          {c?.cdrNumber && !filed && !reporting && (
            <button type="button" className="btn btn-outline-secondary" id="btn-report-problem" onClick={() => setReporting(true)}><i className="fa fa-flag me-2" aria-hidden="true" />Report a problem</button>
          )}
          {filed && <span className="badge bg-warning text-dark align-self-center" id="dispute-filed"><i className="fa fa-flag me-1" aria-hidden="true" />Dispute filed with Customer Care</span>}
        </div>
        {reporting && <ReportProblem cdrNumber={c.cdrNumber} onCancel={() => setReporting(false)} onFiled={() => { setFiled(true); setReporting(false); }} />}
      </div>
    </div>
  );
}

export default function LiveChargingTelemetryPage() {
  const [params, setParams] = useSearchParams();
  const siteId = params.get('site_id');
  const sessionParam = params.get('session_id');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stopping, setStopping] = useState(false);
  const sessionId = session?.id;
  const live = session && ['starting', 'charging', 'stopping'].includes(session.status);

  useEffect(() => {
    let cancelled = false;
    (sessionParam ? api.getSession(sessionParam) : api.getActiveSession())
      .then((s) => !cancelled && setSession(s))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [sessionParam]);

  // Live telemetry refresh (OCPP MeterValues arrive roughly every 5 seconds).
  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      setSession(await api.getSession(sessionId));
    } catch (e) {
      setError(e.message);
    }
  }, [sessionId]);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [live, refresh]);

  const stop = async () => {
    if (!window.confirm('Are you sure you want to stop charging? This will ramp down power and release the connector lock.')) return;
    setStopping(true);
    setError('');
    try {
      const s = await api.stopSession(session.id);
      setSession(s);
      setParams({ session_id: s.id }, { replace: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setStopping(false);
    }
  };

  if (loading) return <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div></div>;
  if (!session) {
    return (
      <>
        {error && <div className="container-fluid pt-4"><div className="alert alert-danger" role="alert">{error}</div></div>}
        <StartPanel siteId={siteId} initialQr={params.get('qr') || ''} autoScan={params.get('scan') === '1'} onPickSite={(id) => setParams({ site_id: id })} onStarted={(s) => { setSession(s); setParams({ session_id: s.id }, { replace: true }); }} />
        <RecentSessions />
      </>
    );
  }
  if (!live) return <SessionSummary s={session} />;

  const { telemetry: t, billing: b, station: st } = session;
  return (
    <div className="container-fluid py-4">
      {error && <div className="alert alert-danger" role="alert" id="session-error">{error}</div>}
      {session.awaitingVehicle && (
        <div className="alert alert-info d-flex align-items-center" role="status" id="awaiting-vehicle-alert">
          <i className="fa fa-plug me-2" aria-hidden="true" />
          <div>
            <strong>Plug in your vehicle to start charging.</strong> The charger is ready and waiting. Nothing is billed until charging starts
            {session.awaitingVehicleUntil ? `; if no vehicle is connected by ${new Date(session.awaitingVehicleUntil).toLocaleTimeString()} the session is cancelled and your hold is released.` : '.'}
          </div>
        </div>
      )}
      {session.billingSuspended && (
        <div className="alert alert-warning" role="alert" id="audit-banner">
          <i className="fa fa-triangle-exclamation me-2" />Unusual meter readings were detected ({session.anomalyFlag}). Billing is suspended and your hold is retained pending a Customer Care audit.
        </div>
      )}

      {/* Session Banner */}
      <div className="card p-4 mb-4 shadow-sm border bg-white" style={{ borderLeft: '6px solid #005293' }}>
        <div className="row align-items-center">
          <div className="col-12 col-md-6 col-lg-7 mb-3 mb-md-0">
            <div className="d-flex align-items-center">
              <div className="pulse-indicator me-3" />
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="badge text-bg-success fw-bold px-3 py-1 me-2" id="session-status">CHARGING IN PROGRESS</span>
                  <span className="text-muted small" id="session-code">{session.sessionCode}</span>
                </div>
                <h2 className="h4 fw-bold mb-0" style={BLUE}>{st.siteName} &bull; Stall {String(st.connectorIndex).padStart(2, '0')}</h2>
                <small className="text-muted">{st.vendor} {st.model} &bull; {PLUG_LABEL[st.connectorType] || st.connectorType} {st.maxPowerKw} kW &bull; OCPP ID: {st.chargePointCode}</small>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-5 text-md-end">
            <button type="button" id="btn-stop-session" className="btn btn-danger fw-bold px-4 py-2 shadow-sm" disabled={stopping || session.status !== 'charging'} onClick={stop}>
              {stopping ? <><span className="spinner-border spinner-border-sm me-2" />Stopping...</> : <><i className="fa fa-stop-circle me-2" aria-hidden="true" />Stop Charging Session</>}
            </button>
          </div>
        </div>
      </div>

      {/* Real-Time KPI Telemetry Cards */}
      <div className="row mb-4">
        <div className="col-12 col-sm-6 col-xl-3 mb-3 mb-xl-0">
          <div className="card p-3 h-100 text-center shadow-sm border bg-white">
            <span className="text-muted small text-uppercase fw-bold mb-2">Battery State of Charge</span>
            <div className="my-2"><span className="display-4 fw-bold" style={BLUE} id="kpi-soc">{t.socPercent == null ? '—' : `${Math.round(t.socPercent)}%`}</span></div>
            <div className="progress" style={{ height: 8, backgroundColor: '#e2e8f0', borderRadius: 4 }}>
              <div id="soc-progress-bar" className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-valuenow={t.socPercent ?? 0} aria-valuemin="0" aria-valuemax="100" style={{ width: `${t.socPercent ?? 0}%`, backgroundColor: '#005293' }} />
            </div>
            <small className="text-muted mt-2 d-block">Target Limit: {t.socTargetLimit}% (Fast Charge)</small>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3 mb-3 mb-xl-0">
          <div className="card p-3 h-100 text-center shadow-sm border bg-white">
            <span className="text-muted small text-uppercase fw-bold mb-2">Active Charging Power</span>
            <div className="my-2"><span className="display-4 fw-bold text-success" id="kpi-power">{t.powerKw.toFixed(1)}</span><span className="h5 text-muted ms-1">kW</span></div>
            <div className="d-flex justify-content-around text-muted small mt-2">
              <span><i className="fa fa-bolt me-1 text-warning" /><strong id="kpi-voltage" className="text-dark">{t.voltageV ?? '—'}</strong> V</span>
              <span><i className="fa fa-wave-square me-1 text-primary" /><strong id="kpi-current" className="text-dark">{t.currentA ?? '—'}</strong> A</span>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3 mb-3 mb-xl-0">
          <div className="card p-3 h-100 text-center shadow-sm border bg-white">
            <span className="text-muted small text-uppercase fw-bold mb-2">Delivered Energy</span>
            <div className="my-2"><span className="display-4 fw-bold text-dark" id="kpi-energy">{t.energyKwh.toFixed(2)}</span><span className="h5 text-muted ms-1">kWh</span></div>
            <small className="text-muted mt-2 d-block">Est. Range Added: <strong className="text-primary" style={BLUE}>+{t.rangeAddedKm} km</strong></small>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card p-3 h-100 text-center shadow-sm border bg-white">
            <span className="text-muted small text-uppercase fw-bold mb-2">Duration &amp; Accrued Bill</span>
            <div className="my-2"><span className="display-4 fw-bold" style={AMBER} id="kpi-cost">{peso(b.total)}</span></div>
            <div className="d-flex justify-content-center align-items-center text-muted small mt-2">
              <i className="fa fa-clock me-1 text-primary" />
              <span>Elapsed Time: <strong id="kpi-duration" className="text-dark">{clock(session.elapsedSeconds)}</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* Live Telemetry Stream Chart & Itemized Billing Ledger */}
      <div className="row">
        <div className="col-12 col-lg-8 mb-4">
          <div className="card p-3 h-100 shadow-sm border bg-white">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0" style={BLUE}><i className="fa fa-chart-line me-2" style={BLUE} />Live Power Delivery Curve (kW)</h5>
              <span className="badge text-bg-secondary small"><i className="fa fa-sync-alt fa-spin me-1" />Refreshes every 5 s</span>
            </div>
            <PowerChart series={session.series} />
          </div>
        </div>
        <div className="col-12 col-lg-4 mb-4">
          <div className="card p-3 h-100 shadow-sm border bg-white">
            <h5 className="fw-bold mb-3" style={BLUE}><i className="fa fa-receipt text-warning me-2" />Accrued Charges Breakdown</h5>
            <div className="table-responsive">
              <table className="table table-sm table-borderless mb-0">
                <tbody>
                  <tr><th scope="row" className="text-muted fw-normal text-start">Session Activation Fee</th><td className="text-end text-dark fw-bold" id="bill-session-fee">{peso(b.sessionFee)}</td></tr>
                  <tr><th scope="row" className="text-muted fw-normal text-start">Energy Charge (<span id="bill-kwh">{b.energyKwh.toFixed(2)}</span> kWh @ {peso(b.energyRate)})</th><td className="text-end text-dark fw-bold" id="bill-energy-cost">{peso(b.energyCost)}</td></tr>
                  <tr><th scope="row" className="text-muted fw-normal text-start">Bay Parking ({b.parkingMinutes} mins)</th><td className={`text-end fw-bold ${b.parkingCost ? 'text-dark' : 'text-success'}`} id="bill-parking-cost">{b.parkingCost ? peso(b.parkingCost) : '₱0.00 (Free)'}</td></tr>
                  <tr><th scope="row" className="text-muted fw-normal text-start">Idle Bay Fee</th><td className="text-end text-success fw-bold" id="bill-idle-cost">{b.idleCost ? peso(b.idleCost) : '₱0.00 (Grace)'}</td></tr>
                  <tr className="border-top"><th scope="row" className="fw-bold text-dark text-start">Subtotal (Net)</th><td className="text-end fw-bold text-dark" id="bill-subtotal">{peso(b.subtotal)}</td></tr>
                  <tr><th scope="row" className="text-muted small fw-normal text-start">VAT (12% Included)</th><td className="text-end text-muted small" id="bill-vat">{peso(b.vat)}</td></tr>
                  <tr className="border-top"><th scope="row" className="h6 fw-bold text-start" style={BLUE}>Total Accrued (PHP)</th><td className="text-end h6 fw-bold" style={AMBER} id="bill-total">{peso(b.total)}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="alert alert-info mt-3 mb-0 small p-2 bg-light border">
              <i className="fa fa-shield-alt me-1 text-primary" />Wallet hold active ({peso(session.holdAmount)}). Outstanding balance will be settled automatically upon unplugging.
            </div>
          </div>
        </div>
      </div>

      {/* Live OCPP MeterValues Table */}
      <div className="row">
        <div className="col-12">
          <div className="card p-3 shadow-sm border bg-white">
            <h5 className="fw-bold mb-3" style={BLUE}><i className="fa fa-list me-2" style={BLUE} />Recent OCPP MeterValues Telemetry Stream</h5>
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0" id="meter-readings-table">
                <thead>
                  <tr className="text-muted small text-uppercase"><th>Timestamp</th><th>Meter (kWh)</th><th>Power (kW)</th><th>Voltage (V)</th><th>Current (A)</th><th>Battery SoC</th><th>Status</th></tr>
                </thead>
                <tbody id="telemetry-rows">
                  {session.readings.map((r) => (
                    <tr key={`${r.at}-${r.meterKwh}`}>
                      <td className="fw-bold text-dark">{hhmmss(r.at)}</td>
                      <td>{r.meterKwh.toFixed(2)} kWh</td>
                      <td className="text-success fw-bold">{r.powerKw.toFixed(1)} kW</td>
                      <td>{r.voltageV ?? '—'} V</td>
                      <td>{r.currentA ?? '—'} A</td>
                      <td><span className="badge text-bg-info">{r.socPercent == null ? '—' : `${Math.round(r.socPercent)}%`}</span></td>
                      <td>{r.status === 'ok' ? <span className="badge badge-available">OK (Sampled)</span> : <span className="badge badge-faulted">Quarantined</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <small className="text-muted mt-2">Last sample {session.series.length ? hhmm(session.series[session.series.length - 1].at) : '—'}</small>
          </div>
        </div>
      </div>
    </div>
  );
}
